import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  useSignIn,
  useSignUp,
  useAuth,
  useUser,
  useClerk,
} from '@clerk/react';

const API_URL =
  process.env.REACT_APP_API_URL || 'https://api.horizontl.io';

const FRONTEND_URL =
  process.env.REACT_APP_FRONTEND_URL || 'https://app.horizontl.io';

const isLocalMarketingHost =
  window.location.hostname === 'localhost' ||
  window.location.hostname === '127.0.0.1' ||
  /^192\.168\./.test(window.location.hostname) ||
  /^10\./.test(window.location.hostname) ||
  /^172\.(1[6-9]|2\d|3[01])\./.test(
    window.location.hostname,
  );

const MARKETING_HOME_URL = isLocalMarketingHost
  ? `http://${window.location.hostname}:8000/`
  : 'https://horizontl.io/';

function BrandHeader({ marginBottom = '20px' }) {
  return (
    <a
      href={MARKETING_HOME_URL}
      aria-label="Go to Horizontl homepage"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '10px',
        marginBottom,
        color: '#014035',
        fontSize: '22px',
        fontWeight: '800',
        letterSpacing: '-0.04em',
        textDecoration: 'none',
      }}
    >
      <img
        src="/favicon.png"
        alt=""
        width="34"
        height="34"
        style={{ objectFit: 'contain' }}
      />
      <span>Horizontl</span>
    </a>
  );
}

function Signup() {
  const [searchParams] = useSearchParams();

  const {
    isLoaded: userLoaded,
    isSignedIn,
    user,
  } = useUser();
  const { getToken } = useAuth();
  const { signUp } = useSignUp();
  const { signIn } = useSignIn();
  const clerk = useClerk();
  const { signOut } = clerk;

  const plan = searchParams.get('plan') || 'support_essential';
  const isVerificationPage = searchParams.get('verify') === '1';
  const isCheckoutPage = searchParams.get('checkout') === '1';
  const isCheckoutCancelled =
    searchParams.get('checkout') === 'cancelled';

  const pendingFromUrl = searchParams.get('pending');
  const createdSessionId =
    searchParams.get('__clerk_created_session');
  const verificationFlow = searchParams.get('flow') || 'signup';
  const verificationPlan =
    searchParams.get('selected_plan') || plan;

  const clerkRedirectStatus =
    searchParams.get('__clerk_status');
  const emailLinkVerification = verificationFlow === 'signin'
    ? signIn?.emailLink?.verification
    : signUp?.verifications?.emailLinkVerification;
  const verificationStatus =
    emailLinkVerification?.status || clerkRedirectStatus;
  const storedPendingToken =
    sessionStorage.getItem('pending_signup_token') || '';
  const storedSignupEmail =
    sessionStorage.getItem('pending_signup_email') || '';
  const [email, setEmail] = useState(storedSignupEmail);
  const [companyName, setCompanyName] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(
    Boolean(storedPendingToken && !isVerificationPage && !isCheckoutPage),
  );
  const [statusMessage, setStatusMessage] = useState('');
  const [emailFlow, setEmailFlow] = useState('signup');
  const [error, setError] = useState(null);
  const [accountChecking, setAccountChecking] = useState(false);
  const [pendingToken, setPendingToken] = useState(
    pendingFromUrl || storedPendingToken,
  );
  const checkoutStarted = useRef(false);
  const authCompletionStarted = useRef(false);

  const planNames = {
    support_essential: 'Support Essential — £299/month',
    super_support: 'Super Support — £599/month',
  };

  const selectedPlanName =
    planNames[plan] || planNames.support_essential;

  /* Redirect only provisioned customers. A verified Clerk identity may still
   * need to select a plan and complete Checkout. */
  useEffect(() => {
    if (
      !userLoaded ||
      !isSignedIn ||
      isVerificationPage ||
      isCheckoutPage ||
      isCheckoutCancelled
    ) return;

    let cancelled = false;

    const checkAccount = async () => {
      try {
        setAccountChecking(true);
        const token = await getToken();

        if (!token) {
          throw new Error('active_session_missing');
        }

        const response = await fetch(`${API_URL}/api/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        let data = {};
        try {
          data = await response.json();
        } catch {
          data = {};
        }

        if (cancelled) return;

        if (response.ok) {
          window.location.replace(`${FRONTEND_URL}/home`);
          return;
        }

        if (
          response.status === 403 ||
          data.detail === 'account_not_provisioned' ||
          data.detail === 'organisation_not_found'
        ) {
          if (!authCompletionStarted.current) {
            sessionStorage.removeItem('pending_signup_token');
            sessionStorage.removeItem('pending_signup_email');
            setPendingToken('');
            setEmailSent(false);
          }

          const signedInEmail =
            user?.primaryEmailAddress?.emailAddress ||
            user?.emailAddresses?.[0]?.emailAddress ||
            '';

          if (signedInEmail) {
            setEmail((currentEmail) =>
              currentEmail || signedInEmail.toLowerCase(),
            );
          }

          try {
            const recoveryResponse = await fetch(
              `${API_URL}/api/incomplete-signup`,
              {
                headers: { Authorization: `Bearer ${token}` },
              },
            );

            if (recoveryResponse.ok) {
              const recoveryData = await recoveryResponse.json();

              if (recoveryData.company_name) {
                setCompanyName((currentCompanyName) =>
                  currentCompanyName || recoveryData.company_name,
                );
              }
            } else {
              console.error(
                'Unable to recover incomplete signup:',
                recoveryResponse.status,
              );
            }
          } catch (recoveryError) {
            console.error(
              'Unable to recover incomplete signup:',
              recoveryError,
            );
          }

          setAccountChecking(false);
          return;
        }

        throw new Error(data.detail || 'account_check_failed');
      } catch (err) {
        if (cancelled) return;
        console.error('Unable to check account status:', err);
        setAccountChecking(false);
        setError('We could not check your account status. Please try again.');
      }
    };

    checkAccount();

    return () => {
      cancelled = true;
    };
  }, [
    userLoaded,
    isSignedIn,
    user,
    getToken,
    isVerificationPage,
    isCheckoutPage,
    isCheckoutCancelled,
  ]);

  /* The verification tab owns the remainder of signup. This works in the
   * original browser, incognito, another browser, or another device without
   * requiring the tab that requested the email to remain open. */
  useEffect(() => {
    if (
      !isVerificationPage ||
      verificationStatus !== 'verified' ||
      !pendingFromUrl ||
      !userLoaded ||
      authCompletionStarted.current
    ) {
      return;
    }

    authCompletionStarted.current = true;

    const confirmWhenSessionIsAvailable = async () => {
      try {
        setError(null);
        setStatusMessage(
          'Checking your verified signup...',
        );

        // Reopened or alternative email links should resume the already
        // verified canonical intent without confirming the identity again.
        const statusResponse = await fetch(
          `${API_URL}/api/signup-status`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ pending_token: pendingFromUrl }),
          },
        );

        let statusData = {};
        try {
          statusData = await statusResponse.json();
        } catch {
          statusData = {};
        }

        const alreadyVerified =
          statusResponse.ok &&
          ['email_verified', 'checkout_pending'].includes(
            statusData.status,
          );

        if (!alreadyVerified) {
          // The session Clerk created for this email link is authoritative.
          // Do not let an unrelated account already active in this browser
          // override the identity that completed this verification link.
          const clerkToken = createdSessionId
            ? null
            : await getToken();
          const requestHeaders = {
            'Content-Type': 'application/json',
          };

          if (clerkToken) {
            requestHeaders.Authorization = `Bearer ${clerkToken}`;
          }

          if (!clerkToken && !createdSessionId) {
            throw new Error(
              'Clerk did not return a completed verification session.',
            );
          }

          const response = await fetch(
            `${API_URL}/api/confirm-signup-verification`,
            {
              method: 'POST',
              headers: requestHeaders,
              body: JSON.stringify({
                pending_token: pendingFromUrl,
                clerk_session_id: createdSessionId || null,
                plan: verificationPlan,
              }),
            },
          );

          let data = {};
          try {
            data = await response.json();
          } catch {
            data = {};
          }

          if (!response.ok) {
            throw new Error(
              data.detail || 'Unable to confirm the verified email.',
            );
          }
        }

        sessionStorage.setItem(
          'pending_signup_token',
          pendingFromUrl,
        );
        setPendingToken(pendingFromUrl);
        setStatusMessage(
          alreadyVerified
            ? 'Email already verified. Resuming secure payment...'
            : 'Email verified. Preparing secure payment...',
        );

        const checkoutResponse = await fetch(
          `${API_URL}/api/create-checkout-session`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              pending_token: pendingFromUrl,
              plan: verificationPlan,
            }),
          },
        );

        let checkoutData = {};
        try {
          checkoutData = await checkoutResponse.json();
        } catch {
          checkoutData = {};
        }

        if (!checkoutResponse.ok || !checkoutData.checkout_url) {
          throw new Error(
            checkoutData.detail ||
              'Email was verified, but secure payment could not be opened.',
          );
        }

        window.location.replace(checkoutData.checkout_url);
      } catch (err) {
        console.error('Direct signup confirmation failed:', err);
        authCompletionStarted.current = false;
        setStatusMessage('');
        setError(
          err?.message ||
            'Email was verified, but signup could not be completed. Please try again.',
        );
      }
    };

    confirmWhenSessionIsAvailable();
  }, [
    isVerificationPage,
    verificationStatus,
    pendingFromUrl,
    createdSessionId,
    userLoaded,
    getToken,
    verificationPlan,
  ]);

  /* Clerk email links are one-time-use. If another valid email already
   * completed this canonical signup, a later link may be reported by Clerk as
   * failed even though Horizontl has a verified intent. Check server state
   * once and resume the existing Checkout; never create another intent. */
  useEffect(() => {
    const clerkLinkFailed =
      verificationStatus === 'failed' ||
      verificationStatus === 'expired' ||
      verificationStatus === 'client_mismatch';

    if (
      !isVerificationPage ||
      !clerkLinkFailed ||
      !pendingFromUrl ||
      authCompletionStarted.current
    ) {
      return;
    }

    authCompletionStarted.current = true;

    const resumeExistingVerifiedSignup = async () => {
      try {
        setError(null);
        setStatusMessage('Checking your verified signup...');

        const statusResponse = await fetch(
          `${API_URL}/api/signup-status`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ pending_token: pendingFromUrl }),
          },
        );

        let statusData = {};
        try {
          statusData = await statusResponse.json();
        } catch {
          statusData = {};
        }

        if (
          !statusResponse.ok ||
          !['email_verified', 'checkout_pending'].includes(
            statusData.status,
          )
        ) {
          throw new Error(
            statusData.detail ||
              'This verification link is no longer valid. Please request a new link.',
          );
        }

        setStatusMessage(
          'Email already verified. Resuming secure payment...',
        );

        sessionStorage.setItem(
          'pending_signup_token',
          pendingFromUrl,
        );
        setPendingToken(pendingFromUrl);

        const checkoutResponse = await fetch(
          `${API_URL}/api/create-checkout-session`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              pending_token: pendingFromUrl,
              plan: verificationPlan,
            }),
          },
        );

        let checkoutData = {};
        try {
          checkoutData = await checkoutResponse.json();
        } catch {
          checkoutData = {};
        }

        if (!checkoutResponse.ok || !checkoutData.checkout_url) {
          throw new Error(
            checkoutData.detail ||
              'Unable to resume secure payment.',
          );
        }

        window.location.replace(checkoutData.checkout_url);
      } catch (err) {
        authCompletionStarted.current = false;
        setStatusMessage('');
        setError(
          err?.message ||
            'Unable to recover this signup. Please start again.',
        );
      }
    };

    resumeExistingVerifiedSignup();
  }, [
    isVerificationPage,
    verificationStatus,
    pendingFromUrl,
    verificationPlan,
  ]);

  /* Resume an already verified Checkout after an explicit user action. */
  useEffect(() => {
    if (
      !isCheckoutPage ||
      checkoutStarted.current
    ) {
      return;
    }

    checkoutStarted.current = true;

    const openStripeCheckout = async () => {
      try {
        setLoading(true);
        setError(null);
        setStatusMessage('Preparing secure checkout...');

        const pendingToken =
          pendingFromUrl ||
          sessionStorage.getItem('pending_signup_token');

        if (!pendingToken) {
          throw new Error('missing_pending_token');
        }

        const requestHeaders = {
          'Content-Type': 'application/json',
        };

        let response;
        let data;

        // The confirmation request from the verification page may still be
        // completing, so retry only the server-owned pending state.
        for (let attempt = 0; attempt < 15; attempt += 1) {
          response = await fetch(
            `${API_URL}/api/create-checkout-session`,
            {
              method: 'POST',
              headers: requestHeaders,
              body: JSON.stringify({
                pending_token: pendingToken,
              }),
            },
          );

          data = await response.json();

          const verificationPending =
            response.status === 425 ||
            data.detail === 'verification_pending' ||
            data.detail === 'email_not_verified';

          if (!verificationPending) break;

          await new Promise((resolve) =>
            window.setTimeout(resolve, 1000),
          );
        }

        if (!response.ok) {
          if (data.detail === 'email_mismatch') {
            throw new Error(
              'The verified email does not match this signup.',
            );
          }

          if (data.detail === 'token_expired') {
            throw new Error(
              'Your signup session has expired. Please start again.',
            );
          }

          if (data.detail === 'already_provisioned') {
            throw new Error(
              'This account has already been created. Please log in.',
            );
          }

          if (data.detail === 'identity_invalid') {
            throw new Error(
              'Your verified identity is no longer available. Please start again.',
            );
          }

          if (data.detail === 'superseded') {
            throw new Error(
              'A newer signup attempt exists for this email. Please use the latest verification email.',
            );
          }

          throw new Error(
            data.detail || 'Unable to create checkout.',
          );
        }

        if (!data.checkout_url) {
          throw new Error(
            'The payment provider did not return a checkout URL.',
          );
        }

        window.location.replace(data.checkout_url);
      } catch (err) {
        console.error('Checkout creation failed:', err);

        checkoutStarted.current = false;
        setLoading(false);

        setError(
          err.message ||
            'Unable to open payment. Please try again.',
        );
      }
    };

    openStripeCheckout();
  }, [
    isCheckoutPage,
    pendingFromUrl,
  ]);

  const handleSubmit = async () => {
    if (!email.trim() || !companyName.trim()) {
      setError(
        'Please enter both your email and company name',
      );
      return;
    }

    if (!signUp || !signIn) {
      setError(
        'Authentication is unavailable. Please refresh and try again.',
      );
      return;
    }

    if (!planNames[plan]) {
      setError('The selected plan is invalid.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      /*
       * This endpoint should only validate the information
       * and create a short-lived pending signup.
       *
       * It must not create a Stripe Checkout Session.
       */
      const response = await fetch(
        `${API_URL}/api/validate-signup`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: email.trim().toLowerCase(),
            company_name: companyName.trim(),
            plan,
          }),
        },
      );

      const data = await response.json();

      if (!response.ok) {
        if (data.detail === 'email_exists') {
          setError(
            'You already have an account. Please log in.',
          );
        } else if (data.detail === 'company_exists') {
          setError(
            'Your company already has a Horizontl account. Ask your administrator to invite you.',
          );
        } else if (data.detail === 'invalid_plan') {
          setError('The selected plan is invalid.');
        } else {
          setError(
            data.detail ||
              'Something went wrong. Please try again.',
          );
        }

        setLoading(false);
        return;
      }

      if (!data.token) {
        throw new Error(
          'The server did not return a pending signup token.',
        );
      }

      // The backend owns canonical identity data after verification. When an
      // existing verified signup resumes, display the stored company rather
      // than any newly entered variation.
      if (data.company_name) {
        setCompanyName(data.company_name);
      }

      const normalizedEmail = email.trim().toLowerCase();

      let useFreshSignInLink = false;

      /* A verified, signed-in but unpaid identity does not need another email
       * link when Clerk can supply an active token. Clerk can temporarily
       * report isSignedIn after its session has become unusable; in that case
       * fall back to a fresh email link instead of blocking plan changes. */
      if (isSignedIn) {
        const signedInEmail = (
          user?.primaryEmailAddress?.emailAddress ||
          user?.emailAddresses?.[0]?.emailAddress ||
          ''
        ).toLowerCase();

        if (!signedInEmail || signedInEmail !== normalizedEmail) {
          throw new Error(
            `You are signed in as ${signedInEmail || 'another account'}. Sign out before continuing with a different email.`,
          );
        }

        let clerkToken = null;
        try {
          clerkToken = await getToken();
        } catch (tokenError) {
          console.warn(
            'Clerk session unavailable; using a fresh sign-in link.',
            tokenError,
          );
        }

        if (clerkToken) {
          sessionStorage.setItem('pending_signup_token', data.token);
          sessionStorage.setItem('pending_signup_email', normalizedEmail);
          setPendingToken(data.token);

          const confirmResponse = await fetch(
            `${API_URL}/api/confirm-signup-verification`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${clerkToken}`,
              },
              body: JSON.stringify({
                pending_token: data.token,
                plan,
              }),
            },
          );
          const confirmData = await confirmResponse.json();

          if (!confirmResponse.ok) {
            if (confirmData.detail === 'email_mismatch') {
              throw new Error(
                'The signed-in email does not match this signup.',
              );
            }

            throw new Error(
              confirmData.detail ||
                'Unable to confirm your verified email.',
            );
          }

          checkoutStarted.current = true;
          const checkoutResponse = await fetch(
            `${API_URL}/api/create-checkout-session`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${clerkToken}`,
              },
              body: JSON.stringify({
                pending_token: data.token,
                plan,
              }),
            },
          );
          const checkoutData = await checkoutResponse.json();

          if (!checkoutResponse.ok || !checkoutData.checkout_url) {
            checkoutStarted.current = false;
            throw new Error(
              checkoutData.detail ||
                'Unable to create secure checkout.',
            );
          }

          window.location.replace(checkoutData.checkout_url);
          return;
        }

        useFreshSignInLink = true;
      }

      let verificationUrl =
        `${FRONTEND_URL}/signup` +
        `?verify=1` +
        `&flow=signup` +
        `&pending=${encodeURIComponent(data.token)}` +
        `&selected_plan=${encodeURIComponent(plan)}`;

      let createError = null;
      if (!useFreshSignInLink) {
        const createResult = await signUp.create({
          emailAddress: normalizedEmail,
        });
        createError = createResult.error;
      }

      const createErrorCode =
        createError?.errors?.[0]?.code || createError?.code;
      const existingClerkIdentity = useFreshSignInLink || [
        'form_identifier_exists',
        'identifier_already_exists',
      ].includes(createErrorCode);

      let linkError;

      if (existingClerkIdentity) {
        setEmailFlow('signin');

        verificationUrl =
          `${FRONTEND_URL}/signup` +
          `?verify=1` +
          `&flow=signin` +
          `&pending=${encodeURIComponent(data.token)}` +
          `&selected_plan=${encodeURIComponent(plan)}`;

        const signInResult = await signIn.emailLink.sendLink({
          emailAddress: normalizedEmail,
          verificationUrl,
        });
        linkError = signInResult.error;
      } else {
        if (createError) throw createError;

        const signUpResult = await signUp.verifications.sendEmailLink({
          verificationUrl,
        });
        linkError = signUpResult.error;
      }

      if (linkError) {
        throw linkError;
      }

      sessionStorage.setItem('pending_signup_token', data.token);
      sessionStorage.setItem('pending_signup_email', normalizedEmail);
      setPendingToken(data.token);

      setEmailSent(true);
      setLoading(false);
      setStatusMessage(
        existingClerkIdentity
          ? 'Open the secure sign-in link to verify your identity and continue to payment. You can safely close this tab.'
          : 'Open the secure verification link to verify your email and continue to payment. You can safely close this tab.',
      );
      return;

    } catch (err) {
      console.error('Signup failed:', err);

      authCompletionStarted.current = false;

      setError(
        err?.errors?.[0]?.longMessage ||
          err?.message ||
          'Something went wrong. Please try again.',
      );
    } finally {
      setLoading(false);
    }
  };

  const handleUseDifferentEmail = async () => {
    setLoading(true);
    setError(null);

    const signupUrl =
      `${FRONTEND_URL}/signup?plan=${encodeURIComponent(verificationPlan)}`;

    sessionStorage.removeItem('pending_signup_token');
    sessionStorage.removeItem('pending_signup_email');

    try {
      if (isSignedIn) {
        await signOut({ redirectUrl: signupUrl });
        return;
      }

      window.location.replace(signupUrl);
    } catch (err) {
      console.error('Unable to clear the Clerk session:', err);
      window.location.replace(signupUrl);
    }
  };

  const S = {
    page: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      width: '100%',
      maxWidth: '100vw',
      padding: '32px 24px',
      overflowX: 'hidden',
      backgroundColor: '#F7FBF9',
      backgroundImage:
        'radial-gradient(circle at 12% 12%, rgba(7, 193, 148, 0.14), transparent 30%), radial-gradient(circle at 88% 88%, rgba(228, 212, 92, 0.18), transparent 28%)',
      fontFamily:
        '-apple-system, BlinkMacSystemFont, "Inter", sans-serif',
      boxSizing: 'border-box',
    },

    card: {
      backgroundColor: '#FFFFFF',
      border: '1px solid #D9E5E1',
      borderRadius: '24px',
      padding: '42px',
      width: '100%',
      maxWidth: '480px',
      margin: '0 auto',
      boxSizing: 'border-box',
      textAlign: 'center',
    },

    planBadge: {
      backgroundColor: '#EFFBF7',
      border: '1px solid #BCE8DB',
      borderRadius: '999px',
      padding: '8px 14px',
      color: '#014035',
      fontSize: '13px',
      fontWeight: '600',
      display: 'inline-block',
      marginBottom: '28px',
      textAlign: 'center',
      width: '100%',
      boxSizing: 'border-box',
    },

    title: {
      fontSize: '34px',
      lineHeight: '1.08',
      letterSpacing: '-0.055em',
      fontWeight: '650',
      color: '#061D18',
      marginBottom: '10px',
      textAlign: 'center',
    },

    subtitle: {
      fontSize: '14px',
      color: '#5B6D67',
      marginBottom: '28px',
      textAlign: 'center',
      lineHeight: '1.5',
    },

    emailAddress: {
      fontSize: '14px',
      color: '#061D18',
      fontWeight: '600',
      marginBottom: '20px',
      textAlign: 'center',
      overflowWrap: 'anywhere',
    },

    label: {
      fontSize: '13px',
      fontWeight: '700',
      color: '#061D18',
      marginBottom: '7px',
      display: 'block',
      textAlign: 'left',
    },

    input: {
      width: '100%',
      minHeight: '48px',
      padding: '0 14px',
      borderRadius: '12px',
      border: '1px solid #C9D8D3',
      fontSize: '15px',
      color: '#061D18',
      boxSizing: 'border-box',
      outlineColor: '#07C194',
      marginBottom: '16px',
    },

    button: {
      width: '100%',
      backgroundColor: '#07C194',
      color: '#014035',
      border: 'none',
      borderRadius: '999px',
      padding: '14px',
      fontSize: '14px',
      fontWeight: '800',
      cursor: loading ? 'not-allowed' : 'pointer',
      opacity: loading ? 0.7 : 1,
      marginTop: '4px',
    },

    secondaryButton: {
      width: '100%',
      backgroundColor: '#FFFFFF',
      color: '#014035',
      border: '1px solid #014035',
      borderRadius: '999px',
      padding: '12px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      marginTop: '16px',
    },

    error: {
      backgroundColor: '#FFF1EE',
      border: '1px solid #C94A2A',
      borderRadius: '12px',
      padding: '12px 16px',
      color: '#C94A2A',
      fontSize: '13px',
      marginBottom: '16px',
      lineHeight: '1.5',
    },

    success: {
      backgroundColor: '#EFFBF7',
      border: '1px solid #BCE8DB',
      borderRadius: '12px',
      padding: '12px 16px',
      color: '#014035',
      fontSize: '13px',
      marginBottom: '16px',
      textAlign: 'center',
      lineHeight: '1.5',
    },

    login: {
      textAlign: 'center',
      fontSize: '13px',
      color: '#5B6D67',
      marginTop: '20px',
    },
  };

  if (isVerificationPage) {
    const clerkVerificationSucceeded =
      verificationStatus === 'verified';

    const verificationSucceeded =
      clerkVerificationSucceeded && !error;

    const verificationFailed =
      verificationStatus === 'failed' ||
      verificationStatus === 'expired' ||
      verificationStatus === 'client_mismatch';

    return (
      <div style={S.page}>
        <div style={S.card}>
          <BrandHeader />

          <div style={S.title}>
            {error && clerkVerificationSucceeded
              ? 'Unable to continue'
              : statusMessage && verificationFailed
                ? 'Resuming signup'
              : verificationSucceeded
                ? 'Email verified'
              : verificationFailed
                ? 'Verification failed'
                : 'Checking your signup'}
          </div>

          <div style={S.subtitle}>
            {error && clerkVerificationSucceeded
              ? 'Your email was verified, but secure payment could not be opened.'
              : statusMessage && verificationFailed
                ? statusMessage
              : verificationSucceeded
                ? (statusMessage || 'Preparing secure payment...')
              : verificationStatus === 'expired'
                ? 'This verification link has expired. Request a new link to continue.'
                : verificationStatus === 'client_mismatch'
                  ? 'This link could not be verified in this browser. Request a new link to continue.'
                  : verificationStatus === 'failed'
                    ? 'We could not verify this email address. Please start again.'
                    : 'Please wait while Clerk verifies your link.'}
          </div>

          {error && (
            <div style={S.error}> {error}</div>
          )}

          {error && clerkVerificationSucceeded && (
            <button
              style={S.secondaryButton}
              onClick={() => window.location.reload()}
            >
              Try secure payment again
            </button>
          )}

          {verificationFailed && (
            <button
              style={S.secondaryButton}
              onClick={handleUseDifferentEmail}
            >
              Start again
            </button>
          )}

        </div>
      </div>
    );
  }

  /*
   * Checkout loading screen.
   */
  if (isCheckoutPage) {
    return (
      <div style={S.page}>
        <div style={S.card}>
          <BrandHeader />

          <div style={S.title}>Preparing payment</div>

          <div style={S.subtitle}>
            {statusMessage ||
              'Opening secure Stripe checkout...'}
          </div>

          {error && (
            <>
              <div style={S.error}> {error}</div>

              <button
                style={S.secondaryButton}
                onClick={handleUseDifferentEmail}
              >
                Start again
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  /*
   * Stripe returned the customer without completing Checkout.
   * Do not automatically reopen Stripe; wait for an explicit action.
   */
  if (isCheckoutCancelled) {
    return (
      <div style={S.page}>
        <div style={S.card}>
          <BrandHeader />

          <div style={S.title}>Payment cancelled</div>

          <div style={S.subtitle}>
            No payment was taken. Your verified signup is still saved.
          </div>

          <button
            style={S.button}
            onClick={() => {
              if (!pendingToken) {
                setError(
                  'Your signup session could not be found. Please start again.',
                );
                return;
              }

              window.location.replace(
                `${FRONTEND_URL}/signup` +
                  `?checkout=1` +
                  `&pending=${encodeURIComponent(pendingToken)}`,
              );
            }}
          >
            Resume secure payment →
          </button>

          <button
            style={{
              ...S.secondaryButton,
              marginTop: '12px',
            }}
            onClick={handleUseDifferentEmail}
          >
            Start a different signup
          </button>

          {error && (
            <div style={{ ...S.error, marginTop: '16px' }}>
              {error}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (accountChecking) {
    return (
      <div style={S.page}>
        <div style={S.card}>
          <BrandHeader />
          <div style={S.title}>Checking your account</div>
          <div style={S.subtitle}>
            Confirming whether your workspace is already active...
          </div>
        </div>
      </div>
    );
  }

  /*
   * Confirmation displayed after Clerk sends the email.
   */
  if (emailSent) {
    return (
      <div style={S.page}>
        <div style={S.card}>
          <BrandHeader />

          <div style={S.title}>
            {emailFlow === 'signin'
              ? 'Sign in to continue'
              : 'Check your email'}
          </div>

          <div style={S.subtitle}>
            {emailFlow === 'signin'
              ? 'We sent a secure sign-in link to:'
              : 'We sent a secure verification link to:'}
          </div>

          <div style={S.emailAddress}>{email}</div>

          <div style={S.success}>
            {statusMessage ||
              (emailFlow === 'signin'
                ? 'Open the secure sign-in link to verify your identity and continue to payment. You can safely close this tab.'
                : 'Open any valid verification email to verify your email and continue to secure payment. You can safely close this tab.')}
          </div>

          {error && (
            <div style={S.error}> {error}</div>
          )}

          <div style={S.subtitle}>
            If you cannot see the email, check your spam or
            junk folder. If the problem continues, contact
            {' '}hello@horizontl.io.
          </div>

          <button
            type="button"
            style={S.secondaryButton}
            onClick={handleUseDifferentEmail}
          >
            Use a different email
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={S.page}>
      <div style={S.card}>
        <BrandHeader />

        <div style={S.title}>Start your free trial</div>

        <div style={S.subtitle}>
          7 days free, then {selectedPlanName}
        </div>

        <div style={S.planBadge}>
          {selectedPlanName}
        </div>

        <label style={S.label}>Work email</label>

        <input
          style={S.input}
          type="email"
          autoComplete="email"
          placeholder="you@company.com"
          value={email}
          onChange={(event) =>
            setEmail(event.target.value)
          }
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              handleSubmit();
            }
          }}
        />

        <label style={S.label}>Company name</label>

        <input
          style={S.input}
          type="text"
          autoComplete="organization"
          placeholder="e.g. Acme Inc"
          value={companyName}
          onChange={(event) =>
            setCompanyName(event.target.value)
          }
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              handleSubmit();
            }
          }}
        />

        {error && (
          <div style={S.error}> {error}</div>
        )}

        <button
          type="button"
          style={S.button}
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? 'Sending link...' : 'Continue →'}
        </button>

        {/* Required by Clerk bot protection for custom sign-up flows. */}
        <div id="clerk-captcha" />

        <div style={S.login}>
          Already have an account?{' '}
          <a
            href={`${FRONTEND_URL}/login`}
            style={{
              color: '#014035',
              fontWeight: '800',
              textUnderlineOffset: '3px',
            }}
          >
            Log in
          </a>
        </div>
      </div>
    </div>
  );
}

export default Signup;